import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  CreditCard, 
  Check, 
  Clock, 
  XCircle,
  ChevronLeft,
  Loader2,
  ExternalLink,
  RefreshCw,
  Calendar,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const styles = {
    paid: "bg-lws-green/10 text-lws-green",
    pending: "bg-lws-amber/10 text-lws-amber",
    failed: "bg-lws-rose/10 text-lws-rose",
    refunded: "bg-lws-ink/10 text-lws-taupe"
  };
  
  const icons = {
    paid: Check,
    pending: Clock,
    failed: XCircle,
    refunded: RefreshCw
  };
  
  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs uppercase tracking-wider ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const SubscriptionCard = ({ subscription, onManage }) => {
  const isExpiringSoon = subscription.needs_renewal;
  
  return (
    <div className={`bg-white border ${isExpiringSoon ? 'border-lws-amber' : 'border-lws-ink/10'} p-6`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-serif text-lg text-lws-ink">{subscription.name}</h3>
          <p className="text-lws-green font-medium mt-1">{subscription.price}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider ${
          subscription.status === 'active' || subscription.status === 'completed'
            ? 'bg-lws-green/10 text-lws-green'
            : 'bg-lws-ink/5 text-lws-taupe'
        }`}>
          {subscription.status}
        </span>
      </div>
      
      {subscription.expires_at && (
        <div className="mt-4 pt-4 border-t border-lws-ink/5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-lws-taupe">
              {isExpiringSoon ? (
                <span className="flex items-center gap-1 text-lws-amber">
                  <AlertCircle className="w-4 h-4" />
                  Renews in {subscription.days_until_renewal} days
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Renews {new Date(subscription.expires_at).toLocaleDateString()}
                </span>
              )}
            </span>
            <button
              onClick={onManage}
              className="text-lws-green hover:text-lws-navy text-xs uppercase tracking-wider"
            >
              Manage
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const PaymentHistoryPage = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [historyRes, subsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/membership/payment-history`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/membership/subscriptions`, { withCredentials: true })
      ]);
      setTransactions(historyRes.data.transactions);
      setSubscriptions(subsRes.data.subscriptions);
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/membership/customer-portal`,
        {},
        { withCredentials: true }
      );
      window.open(response.data.portal_url, '_blank');
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Unable to open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif text-lws-ink mb-4">Please Sign In</h1>
          <a href="#login" className="text-lws-green hover:underline">Log in to view your payment history</a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lws-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lws-cream" data-testid="payment-history-page">
      {/* Header */}
      <div className="bg-lws-green text-lws-paper py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <a href="#membership" className="inline-flex items-center gap-2 text-lws-paper/70 hover:text-lws-paper mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Membership
          </a>
          <h1 className="text-4xl font-serif">Billing & Payments</h1>
          <p className="text-lws-paper/80 mt-2">Manage your subscriptions and view payment history</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Active Subscriptions */}
        {subscriptions.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-lws-ink">Active Subscriptions</h2>
              <button
                onClick={openCustomerPortal}
                disabled={portalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-lws-green text-lws-green hover:bg-lws-green hover:text-lws-paper transition-colors text-xs uppercase tracking-wider"
                data-testid="manage-billing-btn"
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4" />
                    Manage Billing
                  </>
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscriptions.map((sub) => (
                <SubscriptionCard 
                  key={sub.type} 
                  subscription={sub} 
                  onManage={openCustomerPortal}
                />
              ))}
            </div>
          </section>
        )}

        {/* Payment History */}
        <section>
          <h2 className="text-2xl font-serif text-lws-ink mb-6">Payment History</h2>
          
          {transactions.length === 0 ? (
            <div className="bg-white border border-lws-ink/10 p-12 text-center">
              <CreditCard className="w-12 h-12 text-lws-taupe mx-auto mb-4" />
              <h3 className="text-lg font-serif text-lws-ink mb-2">No payments yet</h3>
              <p className="text-lws-taupe mb-6">Your payment history will appear here once you make a purchase.</p>
              <a
                href="#membership"
                className="inline-flex items-center px-6 py-3 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
              >
                View Membership Options
              </a>
            </div>
          ) : (
            <div className="bg-white border border-lws-ink/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-lws-ink/5">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-lws-taupe">Date</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-lws-taupe">Description</th>
                    <th className="text-right p-4 text-xs uppercase tracking-wider text-lws-taupe">Amount</th>
                    <th className="text-right p-4 text-xs uppercase tracking-wider text-lws-taupe">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <motion.tr 
                      key={tx.id || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-t border-lws-ink/5 hover:bg-lws-ink/5"
                    >
                      <td className="p-4 text-sm text-lws-taupe">
                        {tx.date ? new Date(tx.date).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <span className="text-lws-ink">{tx.description}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-medium text-lws-ink">
                          ${tx.amount?.toFixed(2)} {tx.currency}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <StatusBadge status={tx.status} />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Help Section */}
        <section className="mt-12 bg-lws-paper border border-lws-ink/10 p-8">
          <h3 className="text-lg font-serif text-lws-ink mb-4">Need Help?</h3>
          <p className="text-lws-taupe mb-4">
            If you have questions about a charge or need assistance with your subscription, 
            please contact us at{" "}
            <a href="mailto:post@letterwriterssociety.com.au" className="text-lws-green hover:underline">
              post@letterwriterssociety.com.au
            </a>
          </p>
          <button
            onClick={openCustomerPortal}
            disabled={portalLoading}
            className="text-lws-green hover:text-lws-navy text-sm"
          >
            Or manage your billing directly through Stripe →
          </button>
        </section>
      </div>
    </div>
  );
};

export default PaymentHistoryPage;
